/*
 * 戒导航 · 博客数据统计与排序（jiedh/blog-stats）前台入口
 * =============================================================================
 * 设计依据：doc/博客数据统计与排序设计方案.md §6
 *
 * 本文件只做「扩展既有组件」，**不修改 vendor/**，全部走 flarum/common/extend 的
 * 字符串模块路径（惰性解析，与扩展加载顺序无关）：
 *
 *   1) BlogOverview.listParams        —— 把当前排序写进列表参数，供 refreshParams 比对
 *   2) BlogListState.requestParams    —— 用当前排序覆盖 fof/blog 硬编码的 '-createdAt'
 *   3) BlogOverview.view              —— 把 <BlogSortControl /> 插进 .BlogOverviewButtons
 *   4) BlogOverviewItem.dataItems     —— 列表卡补「阅读 / 点赞」
 *   5) FeaturedBlogItem.dataItems     —— 精选卡补「阅读 / 点赞」
 *   6) CommentPost.actionItems        —— 详情页文章**底部**：左「阅读 + 点赞数」只读统计，
 *                                        右「点赞按钮 + 回复 + 更多」操作区（v1.4 左右分区）
 *   7) BlogItem.show                  —— 详情页加载完成后上报一次阅读（30 分钟服务端去重）
 *   8) BlogItem.onbeforeupdate         —— 站内「文章 → 文章」跳转时重新取数（否则停留在上一篇）
 *   9) BlogItem.articleItems          —— 详情页「相关阅读」推荐（正文底部、评论区上方）
 *
 * ⚠ Flarum 2.x 的 extend 语义（与 1.x 完全不同，见 flarum/common/extend.ts）：
 *   包装器会**先执行原方法**，再把原方法的「返回值」作为回调第一个参数传入；
 *   回调不能再拿到 original 去调用，直接原地修改返回值或 this 即可。
 *
 * 任何一处 extend 目标改名（升级 fof/blog 时可能发生）都只会导致「该扩展静默失效」，
 * 不会拖垮整个论坛前端；回归检查项见设计文档 §9。
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Icon from 'flarum/common/components/Icon';
import extractText from 'flarum/common/utils/extractText';
import CommentPost from 'flarum/forum/components/CommentPost';
import BlogSortControl from './components/BlogSortControl';
import BlogLikeButton, { blogLikeCount } from './components/BlogLikeButton';
import RelatedArticles from './components/RelatedArticles';
import { getSort, toParam } from './sortState';

/* =============================================================================
 * 一、排序状态贯通（列表参数 + 请求参数 + 控件注入）
 * ============================================================================= */

const BLOG_OVERVIEW = 'ext:fof/blog/forum/pages/BlogOverview';
const BLOG_LIST_STATE = 'ext:fof/blog/forum/states/BlogListState';

/**
 * 把排序写进 listParams()。
 *
 * 为什么必须放在这里：
 *   `PaginatedListState.refreshParams(newParams, page)` 只在「参数逐键比较后发生变化」时才真正刷新。
 *   我们把 sort 放进参数对象，排序切换时 refreshParams 才能检测到变化并回到第 1 页。
 */
function extendBlogListParams() {
  extend(BLOG_OVERVIEW, 'listParams', function (_params) {
    // 原方法返回新对象，这里直接补 sort 键
    _params.sort = toParam(getSort());
  });
}

/**
 * 覆盖 BlogListState.requestParams() 里硬编码的 `sort: '-createdAt'`。
 *
 * fof/blog 的原始实现（vendor/fof/blog/js/src/forum/states/BlogListState.ts）：
 *   return { include: [...], filter, sort: '-createdAt' };
 * extend 回调拿到的是这个已构造好的对象，直接改写 sort 即可。
 */
function extendBlogRequestParams() {
  extend(BLOG_LIST_STATE, 'requestParams', function (params) {
    params.sort = toParam(getSort());
  });
}

/**
 * 在 vnode 树里递归寻找 `.BlogOverviewButtons` 容器，并把排序控件插到最前面。
 *
 * 为什么用递归而不是固定索引：
 *   `BlogOverview.view()` 返回 `[hero, div]` 结构，按钮容器嵌套较深，
 *   按类名定位比按层级索引稳定得多（该容器在 vendor 里是**无条件渲染**的，
 *   条件渲染的只有「写文章」按钮与语言下拉，因此注入点稳定）。
 */
function injectSortControl(node, page) {
  if (!node) return false;
  if (Array.isArray(node)) {
    return node.some(child => injectSortControl(child, page));
  }
  if (typeof node !== 'object') return false;
  const vnode = node;
  const className = vnode.attrs?.className;
  if (typeof className === 'string' && className.split(/\s+/).indexOf('BlogOverviewButtons') !== -1) {
    const control = m(BlogSortControl, {
      onchange: () => {
        // 排序变更：回到第 1 页重新拉取（listParams 已带 sort，refreshParams 能感知变化）
        page.list?.refreshParams(page.listParams(), 1);
      }
    });
    const children = vnode.children;
    // children 在 JSX 下通常是数组；只有单个子节点时可能是其他类型，这里统一成数组
    vnode.children = Array.isArray(children) ? [control, ...children] : children == null ? [control] : [control, children];
    return true;
  }
  if (Array.isArray(vnode.children)) {
    return vnode.children.some(child => injectSortControl(child, page));
  }
  return false;
}

/** 把排序控件注入博客列表页（`this` 是 BlogOverview 页面实例） */
function extendBlogOverviewView() {
  extend(BLOG_OVERVIEW, 'view', function (vnode) {
    injectSortControl(vnode, this);
  });
}

/* =============================================================================
 * 二、列表 / 精选卡的「阅读 / 点赞」展示
 * ============================================================================= */

const BLOG_OVERVIEW_ITEM = 'ext:fof/blog/forum/components/BlogOverviewItem';
const FEATURED_BLOG_ITEM = 'ext:fof/blog/forum/components/FeaturedBlogItem';

/** 只声明本文件真正用到的模型能力，避免把 fof/blog 的类型打进我们的 bundle */

/** 数字展示：千分位，避免大数字难读；非数字回退 0 */
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '0';
  return n.toLocaleString();
}

/**
 * 列表普通卡：在「评论数」之后补「阅读 / 点赞」两个统计项。
 *
 * fof/blog 的 dataItems 优先级：createdAt 100 / author 80 / replies 60，
 * 本扩展取 views 50、likes 40（按优先级降序渲染在最后）。
 */
function extendBlogOverviewItem() {
  extend(BLOG_OVERVIEW_ITEM, 'dataItems', function (items) {
    const article = this.attrs.article;
    const views = formatCount(article.attribute('viewCount'));
    const likes = formatCount(article.attribute('likeCount'));
    items.add('views', m("span", {
      className: "BlogList-item-details-views",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.views'))
    }, m(Icon, {
      name: "far fa-eye"
    }), " ", views), 50);
    items.add('likes', m("span", {
      className: "BlogList-item-details-likes",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.likes'))
    }, m(Icon, {
      name: "far fa-thumbs-up"
    }), " ", likes), 40);
  });
}

/**
 * 精选大卡：同样补「阅读 / 点赞」，类名换成 BlogFeatured- 前缀（见 vendor 组件）。
 * 优先级与列表卡一致（50 / 40），保证两种卡片的信息顺序相同。
 */
function extendFeaturedBlogItem() {
  extend(FEATURED_BLOG_ITEM, 'dataItems', function (items) {
    const article = this.attrs.article;
    const views = formatCount(article.attribute('viewCount'));
    const likes = formatCount(article.attribute('likeCount'));
    items.add('views', m("span", {
      className: "BlogFeatured-list-item-details-views",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.views'))
    }, m(Icon, {
      name: "far fa-eye"
    }), " ", views), 50);
    items.add('likes', m("span", {
      className: "BlogFeatured-list-item-details-likes",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.likes'))
    }, m(Icon, {
      name: "far fa-thumbs-up"
    }), " ", likes), 40);
  });
}

/* =============================================================================
 * 三、详情页：文章底部「阅读 + 点赞」+ 阅读上报
 * ============================================================================= */

const BLOG_ITEM = 'ext:fof/blog/forum/pages/BlogItem';

/** 详情页文章能力（在 StatsArticleLike 基础上补本地属性写入） */

/** 文章首帖的最小能力（只声明本文件用到的部分，避免引入核心模型类型） */

/**
 * 详情页文章底部动作排：**左侧只读统计** + **右侧操作按钮**（v1.4 布局）。
 *
 * 用户诉求（v1.4）：
 *   「阅读次数和点赞次数显示在文章底部左侧，底部右侧是操作的功能部分，
 *     增加一个点赞按钮，用户在右侧点赞，在左侧的点赞次数统计加 1。」
 *
 * 具体拆成 actionItems 里的两个 item，再用 CSS flex 分成左右两区：
 *   · jiedhStats（priority 100）—— 左区，只读：「阅读数 · 点赞数」；
 *   · jiedhLike （priority 5）  —— 右区，可点击的点赞按钮，排在「回复」(默认 0) 之前。
 *   左区那条 CSS `li.item-jiedhStats{margin-right:auto}` 会把右区所有 item 推到最右，
 *   从而形成「左统计 / 右操作」，且无需改 footer、无需绝对定位。
 *
 * 为什么点击右侧按钮后左侧数字会立刻 +1：
 *   两者读的是同一份数据 —— BlogLikeButton.toggle() 里 applyCount() 会
 *   `post.pushAttributes({likesCount})`（左区优先读它）并 `article.pushAttributes({likeCount})`，
 *   pushAttributes 会刷新模型 freshness，动作排随即重新渲染，数字立即变化。
 *
 * 为什么从「标题下方」挪到这里（v1.3 起）：
 *   用户反馈 —— 标题下面单独一行阅读数太抢眼，点赞入口应该落在文章读完的地方，
 *   和回复在一起，点完赞立刻能看到累计点赞数。
 *
 * 挂载点选择 CommentPost.actionItems 而不是 BlogItem.postItems：
 *   · postItems 在 BlogItem 页面是「标题 / 日期 / 正文」的纵向列，插在正文之后
 *     仍然是一块独立横条，和下方的回复行分成两行；
 *   · actionItems 正是「回复 / 点赞 / 举报」所在的那一排，天然满足「跟回复在一起」。
 *
 * 为什么要移除 `like` 项：
 *   flarum/likes 的原生点赞项是纯文字，桌面端受 `.Post-actions{opacity:0}` 控制只有
 *   hover 才显形，且游客（canLike=false）时整项不渲染；保留它会和本扩展的点赞按钮重复。
 *   这里 remove 掉，统一由右区的 BlogLikeButton 承担；
 *   样式层还有一条 `li.item-like{display:none}` 兜底（防止扩展注册顺序不同导致移除失败）。
 *
 * 作用范围（两个守卫，缺一不可）：
 *   1. 必须是博客详情页路由 `blogArticle` / `blogArticle.near`；
 *   2. 必须是文章首帖（`post.number() === 1`）——博客详情页下方的评论列表同样是
 *      CommentPost，不能把「阅读数」挂到每条评论上。
 */
function extendBlogArticleStats() {
  extend(CommentPost.prototype, 'actionItems', function (items) {
    const routeName = app.current.get('routeName');
    if (routeName !== 'blogArticle' && routeName !== 'blogArticle.near') return;
    const post = this.attrs.post;
    if (!post || typeof post.number !== 'function' || post.number() !== 1) return;

    // 文章（discussion）模型：viewCount 由本扩展的 DiscussionResource 字段提供，
    // likeCount 由 PostLikeCountListener 维护，两者都在同一份 discussion payload 里。
    const article = typeof post.discussion === 'function' ? post.discussion() : null;
    if (!article) return;

    // 移除原生点赞项（顺序兜底见 CSS：.FoFBlog-Article-Post .Post-actions li.item-like）
    items.remove('like');
    const views = formatCount(article.attribute('viewCount'));
    // 点赞数与右侧按钮共用同一口径（blogLikeCount：首帖 likesCount → 文章 likeCount 兜底）
    const likes = formatCount(blogLikeCount(article, post));

    // ---- 左区：只读统计，不承载任何点击行为 ----
    items.add('jiedhStats', m("div", {
      className: "JiedhBlogStats"
    }, m("span", {
      className: "JiedhBlogStats-item",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.views'))
    }, m(Icon, {
      name: "far fa-eye"
    }), " ", views), m("span", {
      className: "JiedhBlogStats-sep",
      "aria-hidden": "true"
    }, "\xB7"), m("span", {
      className: "JiedhBlogStats-item JiedhBlogStats-likes",
      title: extractText(app.translator.trans('jiedh-blog-stats.forum.likes'))
    }, m(Icon, {
      name: "far fa-thumbs-up"
    }), " ", likes)),
    // 最高优先级 ⇒ 渲染在整排最前（最左），读者读完正文一眼可见
    100);

    // ---- 右区：可点击的点赞按钮；priority 5 让它排在「回复」(0) 之前 ----
    items.add('jiedhLike', m(BlogLikeButton, {
      article: article,
      post: post
    }), 5);
  });
}

/**
 * 上报一次阅读（详情页）。
 *
 * 端点：GET {baseUrl}/blog-stats/view/{discussionId}
 *   · 服务端做可见性 / 博客归属校验，并按「登录用户或 IP」做 30 分钟去重；
 *   · 返回值 { ok:true, data:{ viewCount } } 是库内最新真值，直接覆盖本地属性，
 *     让页头统计条即时反映本次阅读。
 *
 * 失败策略：阅读数不是关键路径，不能打扰用户 —— 抑制核心错误弹窗并 catch 静默失败。
 */
function reportBlogView(article) {
  const id = article?.id?.();
  if (!id) return;
  const base = String(app.forum.attribute('baseUrl') || '').replace(/\/+$/, '');
  app.request({
    method: 'GET',
    url: `${base}/blog-stats/view/${id}`,
    errorHandler: () => undefined
  }).then(res => {
    const count = res?.data?.viewCount;
    if (typeof count !== 'number') return;
    article.pushAttributes({
      viewCount: count
    });
    m.redraw();
  }).catch(() => {
    /* 静默：阅读统计失败不影响阅读 */
  });
}

/**
 * 在 BlogItem.show(article) 之后上报阅读。
 *
 * 为什么挂在 show 而不是 oninit：
 *   show 是 vendor 里**唯一**的「文章数据就绪」入口（预加载文档与 store.find 两条路径
 *   最终都会走它），此时 article.id() 一定可用。
 *
 * 为什么要打标：
 *   show 可能因 m.redraw / 重新获取而被多次调用；同一页面实例 + 同一文章只上报一次，
 *   避免同一访客短时间内的多次渲染都打到后端（虽然服务端也会去重，但没必要浪费请求）。
 */
function extendBlogItemViewReport() {
  // ⚠ Flarum 2 的 extend 回调签名是 (原方法返回值, ...原方法参数)，
  //   show() 返回 void，因此第一个参数是 undefined，文章对象在**第二个**参数上。
  extend(BLOG_ITEM, 'show', function (_returnValue, article) {
    if (!article) return;
    const id = String(article.id());
    if (this.__jiedhBlogViewReported === id) return;
    this.__jiedhBlogViewReported = id;
    reportBlogView(article);
  });
}

/** 详情页组件实例的最小能力（只声明本文件用到的部分，避免引入 vendor 类型） */

/**
 * 「文章 → 文章」站内跳转的兜底：一旦详情页实例被复用，就强制重新取数（2026-10-29）。
 *
 * 现状（已实测，勿再误判）：
 *   核心 `DefaultResolver.makeKey()` = `routeName + JSON.stringify(m.route.param())`，
 *   换文章时 key 变化 → `BlogItem` 会被**销毁重建**，`oninit` 重跑并重新取数，
 *   因此正常情况下本逻辑**不会触发**（`routeId === currentId`），属于零成本兜底。
 *
 * 为什么还保留：
 *   · 排查期曾出现「地址栏已变、正文仍是上一篇」的现象，事后定位为**观测时序问题**
 *     （旧 DOM 已满足「标题非骨架 + 无骨架段落 + 有 6 条推荐」的等待条件，轮询提前退出，
 *     而新的推荐数据还在异步加载），并非组件复用。但组件复用一旦发生（例如自定义
 *     resolver 重写 makeKey、或上游升级改动）就会串文章，这里留一道最小保险。
 *
 * 为什么挂在 onbeforeupdate：
 *   组件被复用时 oninit 不会重跑，但每次路由变化必定伴随一次 redraw，
 *   onbeforeupdate 会被调用，正好在这里比对「URL 里的 id」与「已加载文章的 id」。
 *
 * 为什么必须跳过 loading 中的场景：
 *   首次挂载时 `oninit` 已把 `loading` 置 true 并开始取数，此时 `article` 还是 null，
 *   若不判断 `loading` 就会重复取数（同一条 id 发两次 store.find）。
 *
 * id 口径：
 *   路由参数形如 `13397-jie-se-hou-bi-yan-...`，第一段才是数字主键，
 *   与 vendor `loadBlogItem()` 里 `m.route.param('id').split('-')[0]` 完全一致。
 *
 * 重置状态的意义：
 *   把 `loading` 置回 true、`article` 清空，界面才会回到骨架屏并隐藏旧正文/旧评论，
 *   避免「新文章加载中仍显示旧文章」的错觉；`stream`（评论流）属于旧文章，一并丢弃。
 */
function extendBlogItemOnRouteChange() {
  extend(BLOG_ITEM, 'onbeforeupdate', function () {
    const routeId = String(m.route.param('id') || '').split('-')[0].trim();
    const currentId = this.article && typeof this.article.id === 'function' ? String(this.article.id()) : '';

    // 非详情页 / 已是同一篇（首次渲染、普通重绘）/ oninit 正在取数 → 不处理
    if (!routeId || routeId === currentId || this.loading) return;
    this.loading = true;
    this.found = false;
    this.article = null;
    this.stream = undefined;

    // 复用 vendor 的取数逻辑（预加载文档在导航后已失效，会走 store.find(新 id)）
    this.loadBlogItem();

    // 让本次 render 立刻按「骨架屏」渲染，而不是等异步数据回来才刷新
    m.redraw();
  });
}

/* =============================================================================
 * 四、详情页：相关文章推荐（2026-10-29 用户需求）
 * ============================================================================= */

/**
 * 在 BlogItem.articleItems() 里插入「相关阅读」区块。
 *
 * 为什么挂 articleItems 而不是 postItems / 评论区内部：
 *   vendor 的 BlogItem.articleItems() 产出两块内容 —— `content`（priority 100，
 *   标题 / 头图 / 正文 / 底部统计条）与 `comments`（priority 75，评论区）。
 *   取 80 正好落在两者之间，天然满足「文章正文底部、评论区上方」这个位置要求，
 *   且不需要复刻 vendor 的任何 view 结构（升级 fof/blog 时回归风险最低）。
 *
 * 为什么要传 key：
 *   站内跳转（推荐位点进去 → 另一篇文章）会复用同一个组件实例，
 *   key 带上文章 id 才能让 Mithril 重新挂载、重新取数，避免串文章。
 *
 * loading 期间（article 为 null）不渲染：那时连文章 id 都没有，取数没有意义。
 */
function extendBlogItemRelated() {
  extend(BLOG_ITEM, 'articleItems', function (items) {
    const article = this.article;
    if (!article || typeof article.id !== 'function') return;

    // ⚠️ 不能给这个 vnode 传 key：vendor 的 content/comments 两个 vnode 都没有 key，
    //   Mithril 2 要求同一 fragment 内「要么全有 key、要么全无 key」，混用会抛
    //   TypeError 并让整页 redraw 中断（文章页永远停在骨架屏）。
    //   「换文章不串数据」由组件内部的 onbeforeupdate 处理。
    items.add('jiedhRelated', m(RelatedArticles, {
      article: article
    }), 80);
  });
}

/* =============================================================================
 * 五、注册（Flarum 扩展前端标准入口）
 * ============================================================================= */

app.initializers.add('jiedh-blog-stats', () => {
  // 排序：状态贯通 + 控件注入
  extendBlogListParams();
  extendBlogRequestParams();
  extendBlogOverviewView();

  // 展示：列表卡 / 精选卡 / 详情页文章底部
  extendBlogOverviewItem();
  extendFeaturedBlogItem();
  extendBlogArticleStats();

  // 计数：详情页阅读上报
  extendBlogItemViewReport();

  // 详情页：站内「文章 → 文章」跳转时重新取数（避免显示上一篇）
  extendBlogItemOnRouteChange();

  // 详情页：相关文章推荐（评论区上方）
  extendBlogItemRelated();
});